import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { toast } from '@/hooks/use-toast';

export interface Deliverable {
  id: string;
  deal_id: string;
  campaign_id?: string;
  title: string;
  description?: string;
  due_date: string;
  status: 'pending' | 'in_progress' | 'submitted' | 'approved' | 'rejected';
  deliverable_type: 'content' | 'event' | 'metrics' | 'social_post' | 'video' | 'photo';
  approval_status: 'pending' | 'approved' | 'rejected' | 'revision_needed';
  submitted_date?: string;
  approval_notes?: string;
  file_urls: string[];
  created_at: string;
  updated_at: string;
}

export const useDeliverables = (dealId?: string, campaignId?: string) => {
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useUser();

  const fetchDeliverables = async () => {
    if (!currentUser?.id) return;
    
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('deliverables')
        .select('*')
        .order('due_date', { ascending: true });

      if (dealId) {
        query = query.eq('deal_id', dealId);
      }

      if (campaignId) {
        query = query.eq('campaign_id', campaignId);
      }

      const { data, error } = await query;

      if (error) throw error;

      setDeliverables((data || []) as Deliverable[]);
    } catch (err) {
      console.error('Error fetching deliverables:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch deliverables');
    } finally {
      setLoading(false);
    }
  };

  const createDeliverable = async (deliverableData: Omit<Deliverable, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('deliverables')
        .insert([deliverableData])
        .select()
        .single();

      if (error) throw error;

      setDeliverables(prev => [...prev, data as Deliverable]);
      toast({
        title: "Success",
        description: "Deliverable created successfully",
      });

      return data;
    } catch (err) {
      console.error('Error creating deliverable:', err);
      toast({
        title: "Error",
        description: "Failed to create deliverable",
        variant: "destructive",
      });
      throw err;
    }
  };

  const updateDeliverable = async (deliverableId: string, updates: Partial<Deliverable>) => {
    try {
      const { data, error } = await supabase
        .from('deliverables')
        .update(updates)
        .eq('id', deliverableId)
        .select()
        .single();

      if (error) throw error;

      setDeliverables(prev => 
        prev.map(deliverable => deliverable.id === deliverableId ? data as Deliverable : deliverable)
      );

      toast({
        title: "Success",
        description: "Deliverable updated successfully",
      });

      return data;
    } catch (err) {
      console.error('Error updating deliverable:', err);
      toast({
        title: "Error",
        description: "Failed to update deliverable",
        variant: "destructive",
      });
      throw err;
    }
  };

  const submitDeliverable = async (deliverableId: string, fileUrls?: string[]) => {
    return updateDeliverable(deliverableId, {
      status: 'submitted',
      submitted_date: new Date().toISOString().split('T')[0],
      file_urls: fileUrls || []
    });
  };

  const approveDeliverable = async (deliverableId: string, approvalNotes?: string) => {
    return updateDeliverable(deliverableId, {
      approval_status: 'approved',
      approval_notes: approvalNotes
    });
  };

  const rejectDeliverable = async (deliverableId: string, approvalNotes: string) => {
    return updateDeliverable(deliverableId, {
      approval_status: 'rejected',
      approval_notes: approvalNotes
    });
  };

  const requestRevision = async (deliverableId: string, approvalNotes: string) => {
    return updateDeliverable(deliverableId, {
      approval_status: 'revision_needed',
      approval_notes: approvalNotes
    });
  };

  const getDeliverablesSummary = () => {
    const totalCount = deliverables.length;
    const pendingCount = deliverables.filter(d => d.status === 'pending').length;
    const inProgressCount = deliverables.filter(d => d.status === 'in_progress').length;
    const submittedCount = deliverables.filter(d => d.status === 'submitted').length;
    const approvedCount = deliverables.filter(d => d.approval_status === 'approved').length;
    const overdueCount = deliverables.filter(d => {
      const today = new Date().toISOString().split('T')[0];
      return d.due_date < today && d.status !== 'approved';
    }).length;

    return {
      totalCount,
      pendingCount,
      inProgressCount,
      submittedCount,
      approvedCount,
      overdueCount,
      completionRate: totalCount > 0 ? (approvedCount / totalCount) * 100 : 0
    };
  };

  useEffect(() => {
    fetchDeliverables();
  }, [currentUser?.id, dealId, campaignId]);

  return {
    deliverables,
    loading,
    error,
    fetchDeliverables,
    createDeliverable,
    updateDeliverable,
    submitDeliverable,
    approveDeliverable,
    rejectDeliverable,
    requestRevision,
    getDeliverablesSummary: getDeliverablesSummary(),
  };
};